import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import t from '@jetshop/intl';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import loadable from '@loadable/component';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import get from 'lodash.get';
import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Price } from '../Price';
import { theme } from '../Theme';
import Spinner from '../ui/Spinner';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import MobileRelatedProductsContainer from './MobileRelatedProductsContainer';
import ProductColors from './ProductColors/ProductColors';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import RelatedProductsContainer from './RelatedProductsContainer';
import NotifyWhenBack from './StockStatus/NotifyWhenBack';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { ProductContainer, ProductDetailsWrapper } from './styledComponents';

import { useQuery } from '@apollo/react-hooks';
import USPQuery from './USPQuery.gql';
import striptags from 'striptags';
import { useKlaviyoTracking } from '../../hooks/useKlaviyoTracking';
import ProductReviews from '../Yotpo/ProductReviews';
import ProductRatingsSmall from '../Yotpo/ProductRatingsSmall';
import YotpoUGC from '../Yotpo/YotpoUGC';
import { useYotpoLoyalty } from '../Yotpo/YotpoLoyalty';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import { Link } from 'react-router-dom';

const USP_CATEGORY_ID = 409;

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />
  }
);

const ProductDetailsWrapperStyled = styled(ProductDetailsWrapper)`
  ${theme.below.md} {
    .price {
      margin-bottom: 0.5rem;
    }
  }
`;

const RewardInfo = styled(`div`)`
  text-align: center;
  margin-bottom: 20px;

  a {
    color: #000;
  }
`;

const priceStyle = css`
  .price,
  .new-price,
  .old-price {
    height: max-content;
  }

  .price,
  .new-price {
    display: block;
    font-weight: 600;
  }

  .new-price {
    color: #c60f12;
    font-size: 1.25rem;
  }
  .price {
    color: black;
  }
  .old-price {
    color: #999;
    font-size: 16px;
    font-weight: 300;
  }
`;

const Container = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-top: 3rem;
  max-width: 72.5rem;
  width: 100%;
  position: relative;
  ${theme.below.lg} {
    margin-top: 0;
    padding-top: 2.5rem;
    border-top: 0;
  }
  ${theme.below.md} {
    margin-top: 0;
    padding-top: 1.5rem;
  }
  ${theme.below.xs} {
    margin-top: 0;
    padding-top: 1rem;
  }

  .badges-wrapper {
    & > div > div {
      top: 10px;
      div {
        margin-bottom: 5px;
      }
    }

    > div > div > div {
      font-weight: bold;
      font-size: 0.55rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      width: 50px;
      height: 50px;
      color: black;
      background: none;
      img {
        width: 100%;
        height: auto;
        margin-left: auto;
      }
      ${theme.below.lg} {
        img {
          width: 90%;
          height: auto;
          margin-left: auto;
        }
      }
      ${theme.below.md} {
        img {
          width: 80%;
          height: auto;
          margin-left: auto;
        }
      }

      &.sale-tagg {
        background: none;
      }

      &.member-tagg {
        background: none;
      }

      &.best-price-tagg {
        background: none;
      }

      &.new-tagg {
        background: none;
      }
    }
  }
`;

const NameAndPrice = styled('div')`
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 100%;
  ${theme.below.md} {
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0rem;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 300;
    margin-bottom: 0.35rem;
    ${theme.below.md} {
      margin-bottom: 0;
      text-align: left;
    }
    ${theme.below.sm} {
      font-size: 1.25rem;
    }
  }
`;

const PriceStyle = styled('div')`
  font-size: 1.25rem;
  height: max-content;
  ${theme.below.md} {
    text-align: right;
    min-width: 20%;
  }
  ${theme.below.md} {
    width: 120px;
  }

  &.is-package {
    .new-price {
      color: black;
    }
  }
`;

const UspText = styled('p')`
  color: black;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 1;
  margin-bottom: 20px;
  text-align: center;
  font-style: italic;

  ${theme.below.md} {
    margin-top: 15px;
  }
`;

const BreadcrumbsContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 0;
  ${theme.below.md} {
    top: 0.4rem;
    left: 20px;
  }
`;

const yotpoGalleries = {
  // SE
  1: '6364de0b4440331e59cb8ccf',
  // DK
  2: '6364e1543868621f4b99f3d4',
  // EU
  3: '6364e1543868621f4b99f3d4',
  // NO
  4: '6364e45a63c4581eb3b114bb',
  // DE
  5: '6364e45a63c4581eb3b114bb',
  // US
  7: '6365118a23d04a30677a48cc',
  // UK
  8: '6364e1543868621f4b99f3d4',
  // FR
  9: '6364e1543868621f4b99f3d4'
};

function getBrand(product) {
  const field = product?.customFields?.find(({ key }) => key === 'brand');
  if (field) {
    return field.stringValue;
  }
}

const ProductMeta = ({ product, selectedVariation }) => {
  const { selectedChannel } = useContext(ChannelContext);

  const prod = selectedVariation || product;

  if (!prod) {
    return null;
  }

  const brand = getBrand(product);

  return (
    <Helmet>
      <meta property="og:title" content={product.name} />
      <meta
        property="product:original_price:amount"
        content={prod?.price?.incVat}
      />
      <meta
        property="product:price:amount"
        content={prod?.previousPrice?.incVat}
      />
      <meta
        property="og:price:currency"
        content={selectedChannel.currency.id}
      />
      {product?.images?.length > 0 && (
        <meta property="og:image" content={product.images[0].url} />
      )}
      <meta
        property="product:availability"
        content={prod?.stockStatus?.buyable ? 'instock' : 'oos'}
      />
      {brand && <meta property="product:brand" content={brand} />}
      <meta
        property="product:category"
        content={product?.canonicalCategory && product.canonicalCategory.name}
      />
      <meta
        property="og:url"
        content={`${selectedChannel.url}${product.primaryRoute.path}`}
      />
    </Helmet>
  );
};

const Product = ({ result: { data, loading }, product }) => {
  const { selectedChannel } = useContext(ChannelContext);
  const initialVariant = useVariantFromUrl();
  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: false
  });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  const stockStatus = useStockStatus(selectedVariation || product);
  const images = get(product, 'images', []);

  const video = product?.customFields?.find(({ key }) => key === 'video');

  const parents = getParentOrCanonicalRoute(
    get(data, 'route.parents'),
    product
  );

  const { viewedProduct } = useKlaviyoTracking();

  const useLoyalty = useYotpoLoyalty();
  const { loggedIn } = useAuth();

  const [quantity, setQuanitity] = useState(1);

  // Return early if there is no cache hit
  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const relatedProducts = get(product, 'relatedProducts');

  viewedProduct(product);

  // All countries except SE and NO get price * 10 in points.
  const pointsOnPurchase = [1, 4].includes(selectedChannel.id)
    ? product.price.incVat * quantity
    : product.price.incVat * quantity * 10;

  return (
    <ProductContainer>
      <Container>
        <BreadcrumbsContainer>
          <Breadcrumbs
            parents={parents}
            breadcrumbText={product.breadcrumbText}
            breadcrumbs={product.primaryRoute.breadcrumbs}
          />
        </BreadcrumbsContainer>
        <ProductMeta {...{ product, selectedVariation }} />
        <ProductSection>
          <ImageContainer
            badges={product.badges}
            images={images}
            product={product}
            video={video?.stringValue ? video.stringValue : false}
          />

          <Above breakpoint="md">
            <>
              {relatedProducts && relatedProducts.length > 0 && (
                <RelatedProductsContainer {...{ relatedProducts, loading }} />
              )}
            </>
          </Above>
        </ProductSection>

        <ProductDetailsWrapperStyled>
          <NameAndPrice>
            <h1 data-testid="product-title">
              {product.mainHeader || product.name}
            </h1>
            <PriceStyle className={product.isPackage ? 'is-package' : null}>
              <Price
                previousPrice={(selectedVariation || product).previousPrice}
                price={(selectedVariation || product).price}
                className={priceStyle}
              />
            </PriceStyle>
          </NameAndPrice>
          <ProductRatingsSmall product={product} />

          {product?.isPackage ? (
            <LoadablePackageProduct product={product} />
          ) : (
            <>
              <ProductColors
                mainProductId={product.id}
                productArtNo={product.articleNumber}
                colorName={product.subName}
              />

              {stockStatus.status === 'notifyWhenBack' ? (
                <NotifyWhenBack
                  text={stockStatus.text}
                  articleNumber={(selectedVariation || product).articleNumber}
                />
              ) : (
                <AddToCartForm
                  product={product}
                  variant={selectedVariation}
                  getMissingOptions={getMissingOptions}
                  variantHandler={variantHandler}
                  onQuantityChange={setQuanitity}
                />
              )}
              <Above breakpoint="md">
                <StockStatusIndicator
                  status={stockStatus.status}
                  text={stockStatus.text}
                />
              </Above>
            </>
          )}
          <USP />

          {useLoyalty && pointsOnPurchase > 0 && (
            <RewardInfo>
              {loggedIn ? (
                <>
                  {t.rich('You get {points} points for this purchase', {
                    points: Math.trunc(pointsOnPurchase)
                  })}

                  <br />

                  <Link to="/the-club">
                    {t.rich('Read more about {link} here', {
                      link: (
                        <>
                          <em style={{ fontFamily: 'Heldane Italic' }}>the</em>{' '}
                          club
                        </>
                      )
                    })}
                  </Link>
                </>
              ) : (
                <>
                  {t.rich('{link} and get {points} points for this purchase', {
                    link: <Link to="/login">{t('Login')}</Link>,
                    points: Math.trunc(pointsOnPurchase)
                  })}

                  <br />

                  {t.rich('{link} and get {points} points', {
                    link: <Link to="/signup">{t('Become a member')}</Link>,
                    points: 1500
                  })}
                </>
              )}
            </RewardInfo>
          )}

          <ProductInfo product={product} />

          <ProductInfoAccordion product={product} />

          <Below breakpoint="md">
            <>
              {relatedProducts && relatedProducts.length > 0 && (
                <MobileRelatedProductsContainer
                  {...{ relatedProducts, loading }}
                />
              )}
            </>
          </Below>
        </ProductDetailsWrapperStyled>
        <YotpoUGC
          galleryId={yotpoGalleries[selectedChannel.id] || yotpoGalleries[1]}
          productId={product?.id}
        />
        <ProductReviews product={product} />
      </Container>
    </ProductContainer>
  );
};

function USP() {
  const { error, data, loading } = useQuery(USPQuery, {
    variables: {
      id: USP_CATEGORY_ID
    },
    errorPolicy: 'all'
  });

  if (error || loading) {
    return null;
  }

  const { category } = data;

  if (category?.content?.length > 0) {
    const content = striptags(category.content);
    return <UspText dangerouslySetInnerHTML={{ __html: content }} />;
  } else {
    return <UspText>{t('Product USP')}</UspText>;
  }
}

export default Product;
